<script setup>
import AnButton from "@/components/common/AnButton.vue";
import AnManageButton from "@/components/common/AnManageButton.vue";
import MutualFundDialog from "@/components/dashboard/MutualFundDialog.vue";
import StockDialog from "@/components/dashboard/StockDialog.vue";
import AddStockDialog from "@/components/dashboard/AddStockDialog.vue";
import AddMutualFundDialog from "@/components/dashboard/AddMutualFundDialog.vue";
import AddFixedIncomeDialog from "@/components/dashboard/AddFixedIncomeDialog.vue";
import AddOtherInvestsDialog from "@/components/dashboard/AddOtherInvestsDialog.vue";
import { chartColors, pieChartOptions } from "@/services/constant-services";
import { Chip, Column, DataTable } from "primevue";
import Chart from "primevue/chart";
import { ref, watchEffect, computed } from "vue";

const view = ref("stocks");
const pieChartData = ref({});
const othersDetails = ref([]); // Store sectors grouped in "Others"
const isOthersExpanded = ref(false); // Toggle for collapsible section
const isMFDialogOpen = ref(false);
const isStockDialogOpen = ref(false);
const isAddStockDialogOpen = ref(false);
const isAddMutualFundDialogOpen = ref(false);
const isAddFixedIncomeDialogOpen = ref(false);
const isAddOtherInvestsDialogOpen = ref(false);
const stockDialogMode = ref("BUY"); // Track dialog mode
const mutualFundDialogMode = ref("BUY"); // Track MF dialog mode
const fixedIncomeDialogMode = ref("ADD"); // Track Fixed Income dialog mode
const otherInvestmentsDialogMode = ref("ADD"); // Track Other Investments dialog mode
const dialogData = ref({});

// Pagination for mobile view
const currentPage = ref(1);
const itemsPerPage = ref(10);

const setView = (id) => {
  view.value = id;
  // Reset pagination when switching views
  currentPage.value = 1;
};

const props = defineProps({
  stocks: {
    type: Array,
    default: () => [],
  },
  mutualFunds: {
    type: Array,
    default: () => [],
  },
  fixedIncome: {
    type: Array,
    default: () => [],
  },
  otherInvestments: {
    type: Array,
    default: () => [],
  },
  dob: {
    type: String,
    default: null,
  },
  portfolioSummary: {
    type: Object,
    default: () => null,
  },
});

// Calculate current value using compound interest formula
const calculateCurrentValue = (investment) => {
  const principal = investment.amountInvested;
  const rate = investment.interestRate / 100;
  const dateUpdated = new Date(investment.dateUpdated);
  const currentDate = new Date();
  const timeDiffInYears = (currentDate - dateUpdated) / (365 * 24 * 60 * 60 * 1000);

  // P * (1 + r)^t
  const currentValue = principal * Math.pow(1 + rate, timeDiffInYears);
  return currentValue.toFixed(2);
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-IN');
};

// Abbreviate text to specified length
const abbreviateText = (text, maxLength = 10) => {
  if (!text) return 'N/A';
  return text.length > maxLength ? text.substring(0, maxLength - 2) + '..' : text;
};

const formatMaturityDate = (investment) => {
  if (investment.maturityDate) {
    return formatDate(investment.maturityDate);
  }

  if (investment.instrumentType === 'Retirement' && props.dob) {
    const dobDate = new Date(props.dob);
    const retirementDate = new Date(dobDate);
    retirementDate.setFullYear(dobDate.getFullYear() + 60);
    return formatDate(retirementDate.toISOString().split('T')[0]) + '*';
  }

  return 'N/A';
};

const openMFDialog = (data) => {
  isMFDialogOpen.value = true;
  dialogData.value = data;
};

const openStockDialog = (data) => {
  isStockDialogOpen.value = true;
  dialogData.value = data;
};

const mfList = computed(() => props.mutualFunds.map(mf => mf.fundName));
const mfIds = computed(() => props.mutualFunds.map(mf => mf.mutualFundId));

// Filter out stocks with zero or negative shares
const filteredStocks = computed(() => {
  return props.stocks.filter(stock => stock.numberOfShares > 0);
});

// Filter out mutual funds with zero or negative units
const filteredMutualFunds = computed(() => {
  return props.mutualFunds.filter(mf => (mf.numberOfUnits || 0) > 0);
});

// Filter out fixed income with zero or negative amount invested
const filteredFixedIncome = computed(() => {
  return props.fixedIncome.filter(fi => (fi.amountInvested || 0) > 0);
});

// Filter out other investments with zero or negative amount invested
const filteredOtherInvestments = computed(() => {
  return props.otherInvestments.filter(oi => (oi.amountInvested || 0) > 0);
});

// Add current value calculation to fixed income holdings
const fixedIncomeWithCurrentValue = computed(() => {
  return props.fixedIncome.map(holding => ({
    ...holding,
    currentValue: parseFloat(calculateCurrentValue(holding)),
    investedOn: holding.dateUpdated // Pass the invested date
  }));
});

// Check if user has no holdings at all OR all portfolio values are zero
const hasNoHoldings = computed(() => {
  return filteredStocks.value.length === 0 &&
         filteredMutualFunds.value.length === 0 &&
         filteredFixedIncome.value.length === 0 &&
         filteredOtherInvestments.value.length === 0;
});

// Check if portfolio summary indicates all zero values (like a reset portfolio)
const isPortfolioEmpty = computed(() => {
  // Check if we have summary data but all values are zero
  const hasZeroSummary = props.portfolioSummary &&
    props.portfolioSummary.portfolioTotalMarketVal === 0 &&
    props.portfolioSummary.stockPFMarketVal === 0 &&
    props.portfolioSummary.mutualFundPFMarketVal === 0 &&
    props.portfolioSummary.otherTMV === 0;

  return hasNoHoldings.value || hasZeroSummary;
});

// Get current view's holdings count
const currentViewHoldingsCount = computed(() => {
  if (view.value === 'stocks') return filteredStocks.value.length;
  if (view.value === 'mf') return filteredMutualFunds.value.length;
  if (view.value === 'fixedIncome') return filteredFixedIncome.value.length;
  if (view.value === 'otherInvestments') return filteredOtherInvestments.value.length;
  return 0;
});

// Paginated data for mobile view
const paginatedStocks = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredStocks.value.slice(start, end);
});

const paginatedMutualFunds = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredMutualFunds.value.slice(start, end);
});

const paginatedFixedIncome = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredFixedIncome.value.slice(start, end);
});

const paginatedOtherInvestments = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredOtherInvestments.value.slice(start, end);
});

// Calculate total pages
const totalPages = computed(() => {
  const count = currentViewHoldingsCount.value;
  return Math.ceil(count / itemsPerPage.value);
});

// Pagination methods
const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    // Scroll to top of cards container
    const container = document.querySelector('.mobile-cards-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    goToPage(currentPage.value + 1);
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    goToPage(currentPage.value - 1);
  }
};

const handleAddClick = () => {
  if (view.value === 'stocks') {
    stockDialogMode.value = "BUY";
    isAddStockDialogOpen.value = true;
  } else if (view.value === 'fixedIncome') {
    fixedIncomeDialogMode.value = "ADD";
    isAddFixedIncomeDialogOpen.value = true;
  } else if (view.value === 'otherInvestments') {
    otherInvestmentsDialogMode.value = "ADD";
    isAddOtherInvestsDialogOpen.value = true;
  } else {
    mutualFundDialogMode.value = "BUY";
    isAddMutualFundDialogOpen.value = true;
  }
};

const handleSellClick = () => {
  if (view.value === 'stocks') {
    stockDialogMode.value = "SELL";
    isAddStockDialogOpen.value = true;
  } else if (view.value === 'mf') {
    mutualFundDialogMode.value = "REDEEM";
    isAddMutualFundDialogOpen.value = true;
  } else if (view.value === 'fixedIncome') {
    fixedIncomeDialogMode.value = "WITHDRAW";
    isAddFixedIncomeDialogOpen.value = true;
  } else if (view.value === 'otherInvestments') {
    otherInvestmentsDialogMode.value = "WITHDRAW";
    isAddOtherInvestsDialogOpen.value = true;
  }
};

watchEffect(() => {
  let initialData = [];
  let groupedValues = {};

  if (view.value === 'stocks') {
    const stock = filteredStocks.value.map((item) => ({
      ...item,
      division: item.stockSector,
      currentValue: item.currentPrice * item.numberOfShares
    }));
    initialData = [...stock];
  } else if (view.value === 'mf') {
    const mf = props.mutualFunds.map((item) => ({
      ...item,
      division: item.fundSubType,
      currentValue: (item.numberOfUnits || 0) * (item.currentNAV || 0)
    }));
    initialData = [...mf];
  } else if (view.value === 'fixedIncome') {
    const fixedInc = filteredFixedIncome.value.map((item) => {
      const principal = item.amountInvested;
      const rate = item.interestRate / 100;
      const dateUpdated = new Date(item.dateUpdated);
      const currentDate = new Date();
      const timeDiffInYears = (currentDate - dateUpdated) / (365 * 24 * 60 * 60 * 1000);
      const currentValue = principal * Math.pow(1 + rate, timeDiffInYears);

      return {
        ...item,
        division: item.instrumentType,
        currentValue: currentValue
      };
    });
    initialData = [...fixedInc];
  } else if (view.value === 'otherInvestments') {
    const otherInv = filteredOtherInvestments.value.map((item) => ({
      ...item,
      division: item.investmentType,
      currentValue: item.currentVal || 0
    }));
    initialData = [...otherInv];
  }

  // Group by division and sum current values
  initialData.forEach((item) => {
    if (item && typeof item === "object" && item.division) {
      const type = item.division;
      groupedValues[type] = (groupedValues[type] || 0) + item.currentValue;
    }
  });

  const totalValue = Object.values(groupedValues).reduce((sum, val) => sum + val, 0);

  let labels = [];
  let percentages = [];
  let colors = [];

  // For stocks view, show only top 8 sectors and group rest into "Others"
  if (view.value === 'stocks') {
    // Sort sectors by value in descending order
    const sortedSectors = Object.entries(groupedValues)
      .sort((a, b) => b[1] - a[1]);

    if (sortedSectors.length <= 8) {
      // Show all sectors if 8 or fewer
      labels = sortedSectors.map(([label]) => label);
      percentages = sortedSectors.map(([, value]) =>
        totalValue > 0 ? ((value / totalValue) * 100).toFixed(2) : 0
      );
      colors = chartColors.slice(0, labels.length);
    } else {
      // Show top 8 + Others
      const top8 = sortedSectors.slice(0, 8);
      const others = sortedSectors.slice(8);

      labels = top8.map(([label]) => label);
      percentages = top8.map(([, value]) =>
        totalValue > 0 ? ((value / totalValue) * 100).toFixed(2) : 0
      );

      // Calculate "Others" percentage and store details
      const othersValue = others.reduce((sum, [, value]) => sum + value, 0);
      const othersPercentage = totalValue > 0 ? ((othersValue / totalValue) * 100).toFixed(2) : 0;

      if (othersValue > 0) {
        labels.push('Others');
        percentages.push(othersPercentage);

        // Store details of sectors in "Others" for collapsible section
        othersDetails.value = others.map(([sector, value]) => ({
          sector,
          percentage: totalValue > 0 ? ((value / totalValue) * 100).toFixed(2) : 0
        }));
      } else {
        othersDetails.value = [];
      }

      colors = chartColors.slice(0, labels.length);
    }
  } else {
    // For other views, show all divisions
    labels = Object.keys(groupedValues);
    percentages = labels.map((label) => {
      const value = groupedValues[label];
      return totalValue > 0 ? ((value / totalValue) * 100).toFixed(2) : 0;
    });
    colors = chartColors.slice(0, labels.length);
    othersDetails.value = []; // No "Others" for non-stock views
  }

  pieChartData.value = {
    labels,
    datasets: [
      {
        data: percentages,
        backgroundColor: colors,
      },
    ],
  };
});
</script>

<template>
  <MutualFundDialog
    v-if="isMFDialogOpen"
    v-model:isMFDialogOpen="isMFDialogOpen"
    :fund="dialogData"
  />
  <StockDialog
    v-if="isStockDialogOpen"
    v-model:isStockDialogOpen="isStockDialogOpen"
    :stock="dialogData"
  />
  <AddStockDialog
    v-model="isAddStockDialogOpen"
    :mode="stockDialogMode"
    :userHoldings="filteredStocks"
    @update:mode="stockDialogMode = $event"
  />
  <AddMutualFundDialog
    v-model:modelValue="isAddMutualFundDialogOpen"
    :mode="mutualFundDialogMode"
    :userHoldings="filteredMutualFunds"
    @update:mode="mutualFundDialogMode = $event"
  />
  <AddFixedIncomeDialog
    v-model="isAddFixedIncomeDialogOpen"
    :mode="fixedIncomeDialogMode"
    :userHoldings="fixedIncomeWithCurrentValue"
    @update:mode="fixedIncomeDialogMode = $event"
  />
  <AddOtherInvestsDialog
    v-model="isAddOtherInvestsDialogOpen"
    :mode="otherInvestmentsDialogMode"
    :userHoldings="otherInvestments"
    @update:mode="otherInvestmentsDialogMode = $event"
  />

  <div class="d-flex mt-20 justify-between">
    <div class="d-flex gap-20">
      <AnButton
        label="Stocks"
        :variant="view === 'stocks' ? 'primary' : 'tab'"
        @click="() => setView('stocks')"
      />
      <AnButton
        label="Mutual Funds"
        :variant="view === 'mf' ? 'primary' : 'tab'"
        @click="() => setView('mf')"
      />
      <AnButton
        label="Fixed Income"
        :variant="view === 'fixedIncome' ? 'primary' : 'tab'"
        @click="() => setView('fixedIncome')"
      />
      <AnButton
        label="Other Investments"
        :variant="view === 'otherInvestments' ? 'primary' : 'tab'"
        @click="() => setView('otherInvestments')"
      />
    </div>
    <AnManageButton
      label="Manage"
      variant="static"
      icon="pi pi-cog"
      class="gap-10"
      icon-class="font-15"
      @click="handleAddClick"
    />
  </div>

  <!-- Empty State for New Users or Users with All Zero Portfolio Values -->
  <div v-if="isPortfolioEmpty" class="empty-state-container">
    <div class="empty-state-header">
      <i class="pi pi-chart-line text-primary" style="font-size: 3rem;"></i>
      <h2 class="text-primary font-weight-700 mt-15 mb-10">Start Building Your Portfolio</h2>
      <p class="text-muted font-16">Track all your investments here. Add your holdings to see real-time insights and performance analytics.</p>
    </div>

    <div class="investment-categories">
      <div class="category-card" @click="() => { setView('stocks'); handleAddClick(); }">
        <div class="category-icon stocks">
          <i class="pi pi-trending-up"></i>
        </div>
        <h3>Stocks & Shares</h3>
        <p>Add your equity investments and track real-time performance</p>
        <div class="category-button" @click.stop>
          <AnButton label="Add Stocks & Shares" variant="primary" @click="() => { setView('stocks'); handleAddClick(); }" />
        </div>
      </div>

      <div class="category-card" @click="() => { setView('mf'); handleAddClick(); }">
        <div class="category-icon mutual-funds">
          <i class="pi pi-wallet"></i>
        </div>
        <h3>Mutual Funds</h3>
        <p>Track SIP investments and monitor NAV changes</p>
        <div class="category-button" @click.stop>
          <AnButton label="Add Mutual Funds" variant="primary" @click="() => { setView('mf'); handleAddClick(); }" />
        </div>
      </div>

      <div class="category-card" @click="() => { setView('fixedIncome'); handleAddClick(); }">
        <div class="category-icon fixed-income">
          <i class="pi pi-lock"></i>
        </div>
        <h3>Fixed Income</h3>
        <p>Manage FDs, PPF, bonds and other fixed returns</p>
        <div class="category-button" @click.stop>
          <AnButton label="Add Fixed Income" variant="primary" @click="() => { setView('fixedIncome'); handleAddClick(); }" />
        </div>
      </div>

      <div class="category-card" @click="() => { setView('otherInvestments'); handleAddClick(); }">
        <div class="category-icon other-investments">
          <i class="pi pi-star"></i>
        </div>
        <h3>Other Investments</h3>
        <p>Add gold, real estate and alternative investments</p>
        <div class="category-button" @click.stop>
          <AnButton label="Add Other Investments" variant="primary" @click="() => { setView('otherInvestments'); handleAddClick(); }" />
        </div>
      </div>
    </div>
  </div>

  <!-- Empty State for Current View (if user has some holdings but none in current view) -->
  <div v-else-if="currentViewHoldingsCount === 0" class="view-empty-state">
    <!-- Stocks - Use attractive landscape card -->
    <div v-if="view === 'stocks'" class="landscape-card-container">
      <div class="landscape-card stocks-card" @click="handleAddClick">
        <div class="card-left">
          <div class="category-icon stocks">
            <i class="pi pi-trending-up"></i>
          </div>
          <div class="card-content">
            <h3>Stocks & Shares</h3>
            <p class="card-subtitle">Individual company stocks</p>
            <p class="card-description">Own shares of individual companies like Reliance, TCS, HDFC Bank, etc.</p>
          </div>
        </div>
        <div class="card-right">
          <div class="risk-level high-risk">High Risk</div>
          <div class="card-details">
            <div class="detail-item">
              <span class="detail-label">Examples:</span>
              <span class="detail-value">Reliance Industries, TCS</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Min. Amount:</span>
              <span class="detail-value">₹100</span>
            </div>
          </div>
          <AnButton label="Add Stocks" variant="primary" @click.stop="handleAddClick" class="compact-btn" />
        </div>
      </div>
    </div>

    <!-- Mutual Funds - Use attractive landscape card -->
    <div v-else-if="view === 'mf'" class="landscape-card-container">
      <div class="landscape-card mutual-funds-card" @click="handleAddClick">
        <div class="card-left">
          <div class="category-icon mutual-funds">
            <i class="pi pi-wallet"></i>
          </div>
          <div class="card-content">
            <h3>Mutual Funds</h3>
            <p class="card-subtitle">Professionally managed funds</p>
            <p class="card-description">Diversified investment portfolios managed by fund managers.</p>
          </div>
        </div>
        <div class="card-right">
          <div class="risk-level medium-risk">Medium Risk</div>
          <div class="card-details">
            <div class="detail-item">
              <span class="detail-label">Examples:</span>
              <span class="detail-value">SBI Bluechip Fund, HDFC Top 100</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Min. Amount:</span>
              <span class="detail-value">₹500</span>
            </div>
          </div>
          <AnButton label="Add Mutual Funds" variant="primary" @click.stop="handleAddClick" class="compact-btn" />
        </div>
      </div>
    </div>

    <!-- Fixed Income - Use attractive landscape card -->
    <div v-else-if="view === 'fixedIncome'" class="landscape-card-container">
      <div class="landscape-card fixed-income-card" @click="handleAddClick">
        <div class="card-left">
          <div class="category-icon fixed-income">
            <i class="pi pi-lock"></i>
          </div>
          <div class="card-content">
            <h3>Fixed Income</h3>
            <p class="card-subtitle">Stable returns</p>
            <p class="card-description">Fixed deposits, bonds, PPF, and other guaranteed return investments.</p>
          </div>
        </div>
        <div class="card-right">
          <div class="risk-level low-risk">Low Risk</div>
          <div class="card-details">
            <div class="detail-item">
              <span class="detail-label">Examples:</span>
              <span class="detail-value">Fixed Deposits, PPF</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Min. Amount:</span>
              <span class="detail-value">₹100</span>
            </div>
          </div>
          <AnButton label="Add Fixed Income" variant="primary" @click.stop="handleAddClick" class="compact-btn" />
        </div>
      </div>
    </div>

    <!-- Other Investments - Use attractive landscape card -->
    <div v-else-if="view === 'otherInvestments'" class="landscape-card-container">
      <div class="landscape-card other-investments-card" @click="handleAddClick">
        <div class="card-left">
          <div class="category-icon other-investments">
            <i class="pi pi-star"></i>
          </div>
          <div class="card-content">
            <h3>Other Investments</h3>
            <p class="card-subtitle">Alternative assets</p>
            <p class="card-description">Gold, real estate, commodities, and other alternative investments.</p>
          </div>
        </div>
        <div class="card-right">
          <div class="risk-level medium-risk">Medium Risk</div>
          <div class="card-details">
            <div class="detail-item">
              <span class="detail-label">Examples:</span>
              <span class="detail-value">Gold, Real Estate</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Min. Amount:</span>
              <span class="detail-value">₹1,000</span>
            </div>
          </div>
          <AnButton label="Add Other Investments" variant="primary" @click.stop="handleAddClick" class="compact-btn" />
        </div>
      </div>
    </div>
  </div>

  <template v-else-if="view === 'stocks' && currentViewHoldingsCount > 0">
    <!-- Desktop Table View -->
    <DataTable
      :value="filteredStocks"
      class="mt-20 desktop-table"
      :pt="{
        headerRow: { class: 'font-weight-500' },
      }"
      :paginator="true"
      :rows="5"
      :rowsPerPageOptions="[5, 10, 15, 20]"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      sortField="stockSymbol"
      :sortOrder="1"
    >
      <Column header="Symbol" field="stockSymbol" class="font-weight-500 text-primary" :sortable="true">
        <template #body="props">
          <div
            class="d-flex align-items-center gap-5 cursor-pointer"
            @click="openStockDialog(props.data)"
          >
            {{ props.data.stockSymbol }} <i class="pi pi-link" /></div
          ></template>
      </Column>
      <Column header="CMP">
        <template #body="props">
          {{ formatCurrency(props.data.currentPrice) }}
        </template>
      </Column>
      <Column header="No. Of Shares" field="numberOfShares" />
      <Column header="Avg. Buy Price">
        <template #body="props">
          {{ formatCurrency(props.data.averageBuyPrice) }}
        </template>
      </Column>
      <Column header="Current Value">
        <template #body="props">
          <span class="text-success font-weight-500">
            {{ formatCurrency(props.data.currentPrice * props.data.numberOfShares) }}
          </span>
        </template>
      </Column>
      <Column header="52 Wk High/Low">
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.fiftyTwoWeekHigh) }} / {{ formatCurrency(slotProps.data.fiftyTwoWeekLow) }}
        </template>
      </Column>
      <Column header="Unrealized Profit/Loss">
        <template #body="props">
          {{ parseFloat(props.data.unrealizedProfitLoss).toFixed(2) }} %
        </template></Column
      >
      <Column header="Day Gain / Loss">
        <template #body="props">
          <span
            :style="{
              color:
                props.data.dayGainLoss > 0
                  ? 'var(--an-success)'
                  : 'var(--an-error)',
            }"
          >
            {{ props.data.dayGainLoss }}</span
          >
        </template></Column
      >
    </DataTable>

    <!-- Mobile Card View -->
    <div class="mobile-cards-container mt-20">
      <div
        v-for="stock in paginatedStocks"
        :key="stock.stockSymbol"
        class="mobile-stock-card"
        @click="openStockDialog(stock)"
      >
        <div class="card-header">
          <div class="stock-symbol">
            {{ stock.stockSymbol }}
            <i class="pi pi-link"></i>
          </div>
          <div class="current-value">{{ formatCurrency(stock.currentPrice * stock.numberOfShares) }}</div>
        </div>

        <div class="card-body">
          <div class="metric-row">
            <div class="metric-item">
              <span class="metric-label">CMP</span>
              <span class="metric-value">{{ formatCurrency(stock.currentPrice) }}</span>
            </div>
            <div class="metric-item">
              <span class="metric-label">Shares</span>
              <span class="metric-value">{{ stock.numberOfShares }}</span>
            </div>
          </div>

          <div class="metric-row">
            <div class="metric-item">
              <span class="metric-label">Avg. Buy</span>
              <span class="metric-value">{{ formatCurrency(stock.averageBuyPrice) }}</span>
            </div>
            <div class="metric-item">
              <span class="metric-label">P&L</span>
              <span class="metric-value pnl-value">{{ parseFloat(stock.unrealizedProfitLoss).toFixed(2) }}%</span>
            </div>
          </div>

          <div class="metric-row">
            <div class="metric-item full-width">
              <span class="metric-label">52W High/Low</span>
              <span class="metric-value">{{ formatCurrency(stock.fiftyTwoWeekHigh) }} / {{ formatCurrency(stock.fiftyTwoWeekLow) }}</span>
            </div>
          </div>

          <div class="metric-row">
            <div class="metric-item full-width">
              <span class="metric-label">Day Gain/Loss</span>
              <span
                class="metric-value"
                :style="{
                  color: stock.dayGainLoss > 0 ? 'var(--an-success)' : 'var(--an-error)',
                }"
              >
                {{ stock.dayGainLoss }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Pagination -->
      <div v-if="totalPages > 1" class="mobile-pagination">
        <button
          class="pagination-btn"
          @click="prevPage"
          :disabled="currentPage === 1"
        >
          <i class="pi pi-chevron-left"></i>
        </button>

        <div class="pagination-info">
          <span class="page-indicator">Page {{ currentPage }} of {{ totalPages }}</span>
          <span class="items-indicator">({{ ((currentPage - 1) * itemsPerPage) + 1 }}-{{ Math.min(currentPage * itemsPerPage, filteredStocks.length) }} of {{ filteredStocks.length }})</span>
        </div>

        <button
          class="pagination-btn"
          @click="nextPage"
          :disabled="currentPage === totalPages"
        >
          <i class="pi pi-chevron-right"></i>
        </button>
      </div>
    </div>
  </template>
  <template v-else-if="view === 'mf' && currentViewHoldingsCount > 0">
    <!-- Desktop Table View -->
    <DataTable
      :value="filteredMutualFunds"
      class="mt-20 desktop-table"
      :pt="{
        headerRow: { class: 'font-weight-500' },
      }"
      :paginator="true"
      :rows="5"
      :rowsPerPageOptions="[5, 10, 15, 20]"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
    >
      <Column header="Name" class="font-weight-500 text-primary">
        <template #body="props">
          <div
            class="d-flex align-items-center gap-5 cursor-pointer"
            @click="openMFDialog(props.data)"
          >
            {{ props.data.fundName.split('Fund')[0].trim() }} <i class="pi pi-link" /></div
          ></template>
      </Column>
      <Column header="Amount Invested">
        <template #body="props">
          {{ formatCurrency(props.data.amountInvested) }}
        </template>
      </Column>
      <Column header="Current NAV">
        <template #body="props">
          {{ formatCurrency(props.data.currentNAV) }}
        </template>
      </Column>
      <Column header="Current Value">
        <template #body="props">
          <span class="text-success font-weight-500">
            {{ formatCurrency((props.data.numberOfUnits || 0) * (props.data.currentNAV || 0)) }}
          </span>
        </template>
      </Column>
      <Column header="Gain/Loss">
        <template #body="props">
          {{ parseFloat(props.data.fundXIRR || 0).toFixed(2) }} %
        </template>
      </Column>
      <Column header="Type" field="fundType" />
      <Column header="Sub Type" field="fundSubType" />
    </DataTable>

    <!-- Mobile Card View -->
    <div class="mobile-cards-container mt-20">
      <div
        v-for="mf in paginatedMutualFunds"
        :key="mf.mutualFundId"
        class="mobile-mf-card"
        @click="openMFDialog(mf)"
      >
        <div class="card-header">
          <div class="fund-name">
            {{ mf.fundName.split('Fund')[0].trim() }}
            <i class="pi pi-link"></i>
          </div>
          <div class="fund-type-badge">{{ mf.fundType }}</div>
        </div>

        <div class="card-body">
          <div class="metric-row">
            <div class="metric-item">
              <span class="metric-label">Current Value</span>
              <span class="metric-value current-value">{{ formatCurrency((mf.numberOfUnits || 0) * (mf.currentNAV || 0)) }}</span>
            </div>
            <div class="metric-item">
              <span class="metric-label">Gain/Loss</span>
              <span class="metric-value pnl-value">{{ parseFloat(mf.fundXIRR || 0).toFixed(2) }}%</span>
            </div>
          </div>

          <div class="metric-row">
            <div class="metric-item">
              <span class="metric-label">Amount Invested</span>
              <span class="metric-value">{{ formatCurrency(mf.amountInvested) }}</span>
            </div>
            <div class="metric-item">
              <span class="metric-label">Current NAV</span>
              <span class="metric-value">{{ formatCurrency(mf.currentNAV) }}</span>
            </div>
          </div>

          <div class="metric-row">
            <div class="metric-item">
              <span class="metric-label">Units</span>
              <span class="metric-value">{{ (mf.numberOfUnits || 0).toFixed(3) }}</span>
            </div>
            <div class="metric-item">
              <span class="metric-label">Sub Type</span>
              <span class="metric-value">{{ mf.fundSubType }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Pagination -->
      <div v-if="totalPages > 1" class="mobile-pagination">
        <button
          class="pagination-btn"
          @click="prevPage"
          :disabled="currentPage === 1"
        >
          <i class="pi pi-chevron-left"></i>
        </button>

        <div class="pagination-info">
          <span class="page-indicator">Page {{ currentPage }} of {{ totalPages }}</span>
          <span class="items-indicator">({{ ((currentPage - 1) * itemsPerPage) + 1 }}-{{ Math.min(currentPage * itemsPerPage, filteredMutualFunds.length) }} of {{ filteredMutualFunds.length }})</span>
        </div>

        <button
          class="pagination-btn"
          @click="nextPage"
          :disabled="currentPage === totalPages"
        >
          <i class="pi pi-chevron-right"></i>
        </button>
      </div>
    </div>
  </template>

  <template v-else-if="view === 'fixedIncome' && currentViewHoldingsCount > 0">
    <!-- Desktop Table View -->
    <DataTable
      :value="filteredFixedIncome"
      class="mt-20 desktop-table fixed-income-table"
      :pt="{
        headerRow: { class: 'font-weight-500 compact-header' },
      }"
      :paginator="true"
      :rows="5"
      :rowsPerPageOptions="[5, 10, 15, 20]"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
    >
      <Column header="Type" field="investmentType" class="font-weight-500 text-primary compact-col" />
      <Column header="Instrument/Issuer" class="compact-col">
        <template #body="props">
          <div class="d-flex flex-direction-column">
            <span class="font-weight-500 compact-text" :title="props.data.instrumentType">
              {{ abbreviateText(props.data.instrumentType, 15) }}
            </span>
            <span class="text-sm text-muted compact-text" :title="props.data.instrumentIssuer">
              {{ abbreviateText(props.data.instrumentIssuer, 15) }}
            </span>
          </div>
        </template>
      </Column>
      <Column header="Invested" class="compact-col">
        <template #body="props">
          <span class="compact-text">{{ formatCurrency(props.data.amountInvested) }}</span>
        </template>
      </Column>
      <Column header="Current" class="compact-col">
        <template #body="props">
          <span class="text-success font-weight-500 compact-text">
            {{ formatCurrency(calculateCurrentValue(props.data)) }}
          </span>
        </template>
      </Column>
      <Column header="Rate" class="compact-col">
        <template #body="props">
          <span class="compact-text">{{ props.data.interestRate }}%</span>
        </template>
      </Column>
      <Column header="Invested On" class="compact-col">
        <template #body="props">
          <span class="compact-text">{{ formatDate(props.data.dateUpdated) }}</span>
        </template>
      </Column>
      <Column header="Maturity" class="compact-col">
        <template #body="props">
          <span class="compact-text">{{ formatMaturityDate(props.data) }}</span>
        </template>
      </Column>
    </DataTable>

    <!-- Mobile Card View -->
    <div class="mobile-cards-container mt-20">
      <div
        v-for="fi in paginatedFixedIncome"
        :key="fi.instrumentId"
        class="mobile-fi-card"
      >
        <div class="fi-header">
          <div class="fi-title">
            <div class="investment-type">{{ fi.investmentType }}</div>
            <div class="instrument-details">
              {{ fi.instrumentType }}<br>
              <span class="text-muted">{{ fi.instrumentIssuer }}</span>
            </div>
          </div>
          <div class="interest-rate">{{ fi.interestRate }}%</div>
        </div>

        <div class="card-body">
          <div class="metric-row">
            <div class="metric-item">
              <span class="metric-label">Current Value</span>
              <span class="metric-value current-value">{{ formatCurrency(calculateCurrentValue(fi)) }}</span>
            </div>
            <div class="metric-item">
              <span class="metric-label">Amount Invested</span>
              <span class="metric-value">{{ formatCurrency(fi.amountInvested) }}</span>
            </div>
          </div>

          <div class="metric-row">
            <div class="metric-item">
              <span class="metric-label">Invested On</span>
              <span class="metric-value">{{ formatDate(fi.dateUpdated) }}</span>
            </div>
            <div class="metric-item">
              <span class="metric-label">Maturity Date</span>
              <span class="metric-value">{{ formatMaturityDate(fi) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Pagination -->
      <div v-if="totalPages > 1" class="mobile-pagination">
        <button
          class="pagination-btn"
          @click="prevPage"
          :disabled="currentPage === 1"
        >
          <i class="pi pi-chevron-left"></i>
        </button>

        <div class="pagination-info">
          <span class="page-indicator">Page {{ currentPage }} of {{ totalPages }}</span>
          <span class="items-indicator">({{ ((currentPage - 1) * itemsPerPage) + 1 }}-{{ Math.min(currentPage * itemsPerPage, filteredFixedIncome.length) }} of {{ filteredFixedIncome.length }})</span>
        </div>

        <button
          class="pagination-btn"
          @click="nextPage"
          :disabled="currentPage === totalPages"
        >
          <i class="pi pi-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Footnote for retirement maturity dates -->
    <div v-if="filteredFixedIncome.some(item => item.instrumentType === 'Retirement')" class="mt-10">
      <small class="text-muted">* Tentative retirement date based on age 60</small>
    </div>
  </template>

  <template v-else-if="view === 'otherInvestments' && currentViewHoldingsCount > 0">
    <!-- Desktop Table View -->
    <DataTable
      :value="filteredOtherInvestments"
      class="mt-20 desktop-table"
      :pt="{
        headerRow: { class: 'font-weight-500' },
      }"
      :paginator="true"
      :rows="5"
      :rowsPerPageOptions="[5, 10, 15, 20]"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
    >
      <Column header="Investment Type" field="investmentType" class="font-weight-500 text-primary" />
      <Column header="Amount Invested">
        <template #body="props">
          {{ formatCurrency(props.data.amountInvested) }}
        </template>
      </Column>
      <Column header="Current Value">
        <template #body="props">
          <span class="text-success font-weight-500">
            {{ formatCurrency(props.data.currentVal) }}
          </span>
        </template>
      </Column>
      <Column header="Invested On">
        <template #body="props">
          {{ formatDate(props.data.investedDate) }}
        </template>
      </Column>
      <Column header="Last Updated">
        <template #body="props">
          {{ formatDate(props.data.lastUpdated) }}
        </template>
      </Column>
    </DataTable>

    <!-- Mobile Card View -->
    <div class="mobile-cards-container mt-20">
      <div
        v-for="oi in paginatedOtherInvestments"
        :key="oi.investmentType + oi.investedDate"
        class="mobile-oi-card"
      >
        <div class="oi-header">
          <div class="investment-type-name">{{ oi.investmentType }}</div>
          <div class="current-value">{{ formatCurrency(oi.currentVal) }}</div>
        </div>

        <div class="card-body">
          <div class="metric-row">
            <div class="metric-item">
              <span class="metric-label">Amount Invested</span>
              <span class="metric-value">{{ formatCurrency(oi.amountInvested) }}</span>
            </div>
            <div class="metric-item">
              <span class="metric-label">Gain/Loss</span>
              <span class="metric-value pnl-value">{{ ((oi.currentVal - oi.amountInvested) / oi.amountInvested * 100).toFixed(2) }}%</span>
            </div>
          </div>

          <div class="metric-row">
            <div class="metric-item">
              <span class="metric-label">Invested On</span>
              <span class="metric-value">{{ formatDate(oi.investedDate) }}</span>
            </div>
            <div class="metric-item">
              <span class="metric-label">Last Updated</span>
              <span class="metric-value">{{ formatDate(oi.lastUpdated) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Pagination -->
      <div v-if="totalPages > 1" class="mobile-pagination">
        <button
          class="pagination-btn"
          @click="prevPage"
          :disabled="currentPage === 1"
        >
          <i class="pi pi-chevron-left"></i>
        </button>

        <div class="pagination-info">
          <span class="page-indicator">Page {{ currentPage }} of {{ totalPages }}</span>
          <span class="items-indicator">({{ ((currentPage - 1) * itemsPerPage) + 1 }}-{{ Math.min(currentPage * itemsPerPage, filteredOtherInvestments.length) }} of {{ filteredOtherInvestments.length }})</span>
        </div>

        <button
          class="pagination-btn"
          @click="nextPage"
          :disabled="currentPage === totalPages"
        >
          <i class="pi pi-chevron-right"></i>
        </button>
      </div>
    </div>
  </template>

  <!-- Hide chart section when portfolio is empty or has no meaningful data -->
  <div v-if="!isPortfolioEmpty && currentViewHoldingsCount > 0" class="d-flex flex-direction-column">
    <span class="font-weight-500 bg-primary text-center pv-8 ph-10 text-white division-header">
      {{
        view === "stocks" ? "Stocks Sector" :
        view === "mf" ? "Mutual Funds Sector" :
        view === "fixedIncome" ? "Fixed Income Instrument Type" :
        "Other Investments Type"
      }} Division
    </span>
    <div class="d-flex bg-light pv-20">
      <div class="chart-container">
        <Chart
          type="pie"
          :data="pieChartData"
          :options="pieChartOptions"
          :height="300"
          :width="300"
        />
      </div>
      <div class="flex-1 d-flex gap-10 h-max flex-wrap">
        <Chip
          v-for="(label, index) in pieChartData.labels"
          :key="index"
          class="bg-primary p-10 br-10 font-weight-500"
        >
          <span
            class="p-5 br-round mr-5"
            :style="{
              backgroundColor:
                pieChartData.datasets[0].backgroundColor?.[index] ||
                chartColors[index % chartColors.length],
            }"
          ></span>
          <span class="text-white"
            >{{ label }}: {{ pieChartData.datasets[0].data[index] }}%</span
          >
        </Chip>
      </div>
    </div>

    <!-- Collapsible "Others" Details Section (only for stocks with Others) -->
    <div v-if="view === 'stocks' && othersDetails.length > 0" class="others-details-section mt-15">
      <div class="others-header" @click="isOthersExpanded = !isOthersExpanded">
        <div class="others-title-container">
          <span class="collapse-icon" :class="{ 'expanded': isOthersExpanded }">▶</span>
          <span class="others-title">Sectors in "Others"</span>
          <span class="others-count-badge">{{ othersDetails.length }} sectors</span>
        </div>
      </div>

      <div v-show="isOthersExpanded" class="others-content">
        <div class="others-table">
          <div
            v-for="(item, index) in othersDetails"
            :key="item.sector"
            class="others-table-row"
          >
            <div class="sector-cell">{{ item.sector }}</div>
            <div class="percentage-cell">{{ item.percentage }}%</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Empty State Styles */
.empty-state-container {
  padding: 40px 20px;
  text-align: center;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  margin: 30px 0;
}

/* Division Header - Compact */
.division-header {
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  border-radius: 6px 6px 0 0;
}

.pv-8 {
  padding-top: 8px;
  padding-bottom: 8px;
}

.ph-10 {
  padding-left: 10px;
  padding-right: 10px;
}

.empty-state-header {
  margin-bottom: 40px;
}

.investment-categories {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.category-card {
  background: white;
  border-radius: 12px;
  padding: 30px 20px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
}

.category-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border-color: var(--an-primary);
}

.category-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 1.5rem;
  color: white;
}

.category-icon.stocks {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.category-icon.mutual-funds {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.category-icon.fixed-income {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
}

.category-icon.other-investments {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.category-card h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
}

.category-card p {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 20px;
  line-height: 1.5;
}

.category-button {
  display: flex;
  justify-content: center;
  margin-top: auto;
}

/* View Empty State */
.view-empty-state {
  padding: 10px 0;
  margin: 10px 0;
}

.view-empty-content {
  max-width: 400px;
  margin: 0 auto;
}

/* Landscape Card Layout */
.landscape-card-container {
  display: flex;
  justify-content: center;
  margin: 10px 0;
}

.landscape-card {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 800px;
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 2px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
}

.landscape-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.stocks-card:hover {
  border-color: #10b981;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
}

.mutual-funds-card:hover {
  border-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
}

.fixed-income-card:hover {
  border-color: #8b5cf6;
  background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
}

.other-investments-card:hover {
  border-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.card-left {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 16px;
}

.card-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  min-width: 200px;
}

.category-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
  flex-shrink: 0;
}

.category-icon.stocks {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.category-icon.mutual-funds {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.category-icon.fixed-income {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
}

.category-icon.other-investments {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.card-content h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.card-subtitle {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0 0 6px 0;
  font-weight: 500;
}

.card-description {
  font-size: 0.75rem;
  color: #4b5563;
  line-height: 1.3;
  margin: 0;
}

.card-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 0.7rem;
}

.detail-label {
  color: #6b7280;
  font-weight: 600;
  white-space: nowrap;
}

.detail-value {
  color: #1f2937;
  font-weight: 500;
  text-align: right;
}

.risk-level {
  font-size: 0.6rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  color: white;
  white-space: nowrap;
}

.risk-level.high-risk {
  background: #ef4444;
}

.risk-level.medium-risk {
  background: #f59e0b;
}

.risk-level.low-risk {
  background: #10b981;
}

.compact-btn {
  font-size: 0.8rem !important;
  padding: 6px 12px !important;
  min-height: 32px !important;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .landscape-card {
    flex-direction: column;
    text-align: center;
    padding: 12px;
  }

  .card-left {
    flex-direction: column;
    text-align: center;
    margin-bottom: 12px;
  }

  .card-right {
    align-items: center;
    min-width: auto;
  }

  .card-details {
    text-align: center;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .investment-categories {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .category-card {
    padding: 20px 15px;
  }

  .empty-state-container {
    padding: 30px 15px;
  }

  /* Ensure buttons stack properly on mobile */
  .d-flex.mt-20.justify-between {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .d-flex.gap-20 {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    width: 100%;
  }

  /* Make Manage button full width on mobile */
  .d-flex.mt-20.justify-between > .an-manage-button {
    width: 100%;
  }
}

/* DataTable Responsiveness - Fixed width consistency */
.desktop-table {
  display: table !important;
  width: 100%;
  table-layout: auto;
}

/* Fixed Income Table Compact Styles */
.fixed-income-table {
  font-size: 0.875rem;
}

.fixed-income-table :deep(.compact-header th) {
  font-size: 0.8125rem !important;
  padding: 8px 6px !important;
  white-space: nowrap;
}

.fixed-income-table :deep(.compact-col) {
  padding: 8px 6px !important;
}

.fixed-income-table :deep(.compact-text) {
  font-size: 0.8125rem;
}

.fixed-income-table :deep(td) {
  padding: 8px 6px !important;
}

.fixed-income-table :deep(th) {
  padding: 8px 6px !important;
}

.mobile-cards-container {
  display: none !important;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 100%;
}

/* Force mobile view on smaller screens */
@media (max-width: 1024px) {
  .desktop-table,
  .mt-20.desktop-table,
  DataTable.desktop-table {
    display: none !important;
  }

  .mobile-cards-container,
  .mt-20.mobile-cards-container {
    display: flex !important;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
  }
}

/* Ensure consistent container widths */
.section-container {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* Fix chart section width consistency */
.d-flex.flex-direction-column {
  width: 100%;
  max-width: 100%;
}

.d-flex.bg-light.pv-20 {
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 15px 10px;
}

.chart-container {
  flex-shrink: 0;
  width: 300px;
  height: 280px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 20px;
}

/* Mobile Other Investments Cards */
.mobile-oi-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
}

.mobile-oi-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.12);
}

.oi-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f3f4f6;
}

.investment-type-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-primary);
}

/* Mobile Stock Cards - Complete styling restored */
.mobile-stock-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
}

.mobile-stock-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border-color: var(--an-primary);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f3f4f6;
}

.stock-symbol {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--an-primary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.current-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--an-success);
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.metric-row {
  display: flex;
  gap: 16px;
}

.metric-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.metric-item.full-width {
  flex: 1;
}

.metric-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2937;
}

.pnl-value {
  color: var(--an-success);
}

/* Mobile Mutual Fund Cards - Complete styling restored */
.mobile-mf-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
}

.mobile-mf-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border-color: var(--an-primary);
}

.fund-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-primary);
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.fund-type-badge {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #4b5563;
  font-weight: 500;
}

/* Mobile Fixed Income Cards - Complete styling restored */
.mobile-fi-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
}

.mobile-fi-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.12);
}

.fi-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f3f4f6;
}

.fi-title {
  flex: 1;
}

.investment-type {
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-primary);
  margin-bottom: 4px;
}

.instrument-details {
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.3;
}

.interest-rate {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Tablet Responsive */
@media (max-width: 1200px) and (min-width: 769px) {
  .metric-row {
    flex-wrap: wrap;
  }

  .metric-item {
    min-width: 45%;
  }
}

/* Small Mobile Adjustments */
@media (max-width: 480px) {
  .mobile-stock-card,
  .mobile-mf-card,
  .mobile-fi-card,
  .mobile-oi-card {
    padding: 12px;
  }

  .card-header,
  .fi-header,
  .oi-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .metric-row {
    flex-direction: column;
    gap: 8px;
  }

  .metric-item {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .metric-label {
    font-size: 0.8rem;
  }

  .metric-value {
    font-size: 0.85rem;
  }
}

/* Mobile Pagination Styles */
.mobile-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding: 12px 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  gap: 12px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: white;
  color: var(--an-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--an-primary);
  color: white;
  border-color: var(--an-primary);
}

.pagination-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  color: #9ca3af;
}

.pagination-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.page-indicator {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2937;
}

.items-indicator {
  font-size: 0.75rem;
  color: #6b7280;
}

@media (max-width: 480px) {
  .mobile-pagination {
    padding: 10px 12px;
  }

  .pagination-btn {
    width: 36px;
    height: 36px;
    font-size: 0.9rem;
  }

  .page-indicator {
    font-size: 0.85rem;
  }

  .items-indicator {
    font-size: 0.7rem;
  }
}

/* Others Details Collapsible Section */
.others-details-section {
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.others-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  cursor: pointer;
  transition: background 0.2s ease;
  user-select: none;
}

.others-header:hover {
  background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
}

.others-title-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.collapse-icon {
  font-size: 10px;
  color: #495057;
  transition: transform 0.2s ease;
  display: inline-block;
}

.collapse-icon.expanded {
  transform: rotate(90deg);
}

.others-title {
  font-size: 14px;
  font-weight: 600;
  color: #212529;
}

.others-count-badge {
  background: #6c757d;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}

.others-content {
  padding: 16px;
  background: #fafbfc;
  border-top: 1px solid #e5e7eb;
}

.others-table {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px 12px;
  max-width: 100%;
}

.others-table-row {
  display: contents;
}

.sector-cell {
  font-size: 13px;
  font-weight: 500;
  color: #495057;
  padding: 6px 8px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.percentage-cell {
  font-size: 13px;
  font-weight: 600;
  color: #212529;
  padding: 6px 8px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  text-align: right;
}

/* Responsive: 2 columns on narrow screens */
@media (max-width: 768px) {
  .others-table {
    grid-template-columns: 1fr auto;
  }

  .d-flex.bg-light.pv-20 {
    flex-direction: column;
    align-items: center;
  }

  .chart-container {
    width: 100%;
    height: auto;
    max-width: 300px;
    padding: 20px 0;
  }

  /* Stack chips vertically on mobile */
  .flex-1.d-flex.gap-10.h-max.flex-wrap {
    flex-direction: column;
    width: 100%;
    gap: 8px !important;
  }

  .flex-1.d-flex.gap-10.h-max.flex-wrap .p-chip {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 480px) {
  .others-header {
    padding: 10px 12px;
  }

  .others-title {
    font-size: 13px;
  }

  .others-count-badge {
    font-size: 10px;
    padding: 2px 6px;
  }

  .others-content {
    padding: 12px;
  }

  .sector-cell,
  .percentage-cell {
    font-size: 12px;
    padding: 5px 6px;
  }
}
</style>
